import { Layer, LayerExtension } from "@deck.gl/core"

export type ComparisonClip = {
    threshold: number
    axis: 0 | 1
    hideHigh: boolean
} | null

export type OrthoComparisonProps = {
    comparisonClip: ComparisonClip
}

// Fragment shader injection for BitmapLayer.
// Uses vTexCoord (the tile's texture UV) and our own orthoComp_tileBounds uniform
// to compute each fragment's geographic position and discard the hidden side.
// We intentionally avoid referencing the BitmapLayer's existing `bounds` uniform
// because it is not reliably addressable by name from injected extension code in luma.gl 9.
export class OrthoComparisonExtension extends LayerExtension<OrthoComparisonProps> {
    static get componentName() {
        return "OrthoComparisonExtension"
    }
    static defaultProps = {
        comparisonClip: { threshold: 0, axis: 0, hideHigh: false },
    }

    getShaders(): object {
        return {
            inject: {
                "fs:#decl": `
uniform float orthoComp_threshold;
uniform float orthoComp_axis;
uniform float orthoComp_hideHigh;
uniform vec4 orthoComp_tileBounds;
`,
                // geometry.uv is the function parameter of DECKGL_FILTER_COLOR(inout vec4 color, FragmentGeometry geometry).
                // We must NOT reference vTexCoord here — it is declared in the BitmapLayer core source which
                // is appended AFTER the hook function body in the assembled GLSL, making it undeclared at this point.
                "fs:DECKGL_FILTER_COLOR": `
{
  float lng = mix(orthoComp_tileBounds[0], orthoComp_tileBounds[2], geometry.uv.x);
  float lat = mix(orthoComp_tileBounds[3], orthoComp_tileBounds[1], geometry.uv.y);
  float coord = orthoComp_axis < 0.5 ? lng : lat;
  if ((orthoComp_hideHigh > 0.5 && coord > orthoComp_threshold) ||
      (orthoComp_hideHigh <= 0.5 && coord < orthoComp_threshold)) discard;
}
`,
            },
        }
    }

    draw(this: Layer<OrthoComparisonProps & { bounds: [number, number, number, number] }>) {
        const { comparisonClip, bounds } = this.props
        const [model] = this.getModels()
        if (!model || !comparisonClip) return
        if (!Array.isArray(bounds) || bounds.length !== 4) return
        model.setUniforms({
            orthoComp_threshold: comparisonClip.threshold,
            orthoComp_axis: comparisonClip.axis,
            orthoComp_hideHigh: comparisonClip.hideHigh ? 1.0 : 0.0,
            orthoComp_tileBounds: bounds,
        })
    }
}
